import { z } from 'zod';
/**
 * Meaning-based search for project files (v0.9): an embeddings model chosen
 * from an existing provider, used together with the pgvector extension. Both
 * are needed; without either, project search stays keyword-only.
 */
/** Provider kinds that can embed text. Anthropic offers no embeddings API. */
export const EMBEDDING_PROVIDER_KINDS = ['openai', 'google', 'openai-compatible'];
export function providerCanEmbed(kind) {
    return EMBEDDING_PROVIDER_KINDS.includes(kind);
}
/** pgvector supports up to 16,000 dimensions for the `vector` type. */
export const MAX_EMBEDDING_DIMENSIONS = 16000;
/**
 * Whether the database can store vectors.
 * - `not-installed`: the server has no pgvector package; the image must change.
 * - `available`: installed on the server but not enabled in this database.
 * - `enabled`: `CREATE EXTENSION vector` has been run.
 */
export const PGVECTOR_STATES = ['not-installed', 'available', 'enabled'];
export const PGVECTOR_ENABLE_COMMAND = 'CREATE EXTENSION IF NOT EXISTS vector;';
export const embeddingsSettingsSchema = z.object({
    enabled: z.boolean(),
    providerId: z.string().nullable(),
    modelId: z.string().nullable(),
    /** Read from a test embedding when the model is saved. */
    dimensions: z.number().int().positive().nullable(),
    /** Micro-dollars per million input tokens; null records usage at no cost. */
    inputPriceMicros: z.number().int().nonnegative().nullable(),
});
export const updateEmbeddingsSchema = z
    .object({
    enabled: z.boolean().optional(),
    providerId: z.string().trim().min(1).max(200).nullable().optional(),
    modelId: z.string().trim().min(1).max(200).nullable().optional(),
    inputPriceMicros: z.number().int().nonnegative().max(1_000_000_000_000).nullable().optional(),
})
    .strict();
/** Embeds a sample with the model on the page (or the saved one); nothing is stored. */
export const embeddingsTestSchema = z
    .object({
    providerId: z.string().trim().min(1).max(200).optional(),
    modelId: z.string().trim().min(1).max(200).optional(),
})
    .strict();
/**
 * "Switch now" for the generation being filled. `force` switches before it
 * covers every passage; the passages it misses are found by keyword only
 * until the job embeds them.
 */
export const embeddingsSwitchSchema = z
    .object({
    force: z.boolean().optional(),
})
    .strict();
/**
 * Embedding generations (v0.11): each embeddings configuration (provider,
 * model, dimensions) is a generation with its own storage. Changing the model
 * fills a new generation in the background while searches keep using the
 * current one, then switches to it.
 *
 * - `filling`: being embedded; new passages are written to it as well.
 * - `current`: what searches use.
 * - `retired`: searched no more; its storage is dropped after a grace period.
 * - `cancelled`: a rebuild abandoned before its switch.
 * - `dropped`: its storage is gone; kept as history.
 */
export const EMBEDDING_GENERATION_STATES = [
    'filling',
    'current',
    'retired',
    'cancelled',
    'dropped',
];
/** Embedding cost in micro-dollars, or null when no price is known. */
export function embeddingCostMicros(estimate, inputPriceMicros) {
    if (inputPriceMicros === null || inputPriceMicros === undefined)
        return null;
    return Math.ceil((estimate.passages * estimate.averageTokens * inputPriceMicros) / 1_000_000);
}
//# sourceMappingURL=embeddings.js.map